import React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import ActiveFiltersBar from '../ActiveFiltersBar';
import GridActionsMenu from '../GridActionsMenu';
import GridCompactDensitySync from '../GridCompactDensitySync';
import useDashboardCompact from '../../../styles/theme/surfaces/useDashboardCompact';

/**
 * List toolbar for a single-card+DataGrid page, on one row that wraps only
 * when it has to: the domain filters first, then the search taking the rest
 * of the row, then a ⋮ (GridActionsMenu) with the grid's own Columns /
 * Filters / Settings. Search and ⋮ move together, so when the filters don't
 * fit they drop to the next line as one block. The active-filters chip bar
 * comes below. Doubles as the mobile fallback's filter bar
 * (`showGridControls={false}`, rendered outside any DataGrid), so the root is
 * a plain `Box` — NOT `GridToolbarContainer`, which calls `useGridRootProps()`
 * unconditionally and throws when there's no DataGrid ancestor.
 *
 * `filters`: [{ field, label, value, onChange, options: [{value,label}] }]
 * `extraFilters`: node rendered right after the selects, for fields a select
 * can't express (e.g. the start/end dates of a custom period)
 * `search`: { label, value, onChange }
 * `gridSettings`: { showColumnBorder, onShowColumnBorderChange, showCellBorder, onShowCellBorderChange }
 * `endAction`: node right after the search, before the ⋮ (e.g. the
 * grid/cards `ViewModeToggle`).
 * `bulkAction`: node shown right-aligned below the filters (e.g. a "Delete
 * (n)" button) when there's a selection — lives here, not as a sibling of
 * the DataGrid, so it sits below the filters instead of above everything.
 */
export default function ListFilterToolbar({
  filters = [],
  extraFilters,
  search,
  chips = [],
  resultsLabel,
  clearLabel,
  onClearAll,
  showGridControls = true,
  gridSettings,
  endAction,
  bulkAction,
}) {
  // No compacto os filtros encolhem (telas ~1366px).
  const compact = useDashboardCompact();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 2, pt: 2, pb: 1 }}>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2} alignItems="center">
        {filters.map((filter) => (
          <TextField
            key={filter.field}
            select
            label={filter.label}
            value={filter.value}
            onChange={(event) => filter.onChange(event.target.value)}
            sx={{ minWidth: compact ? 150 : 180, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
          >
            {filter.options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        ))}
        {extraFilters}
        {/* Search + ⋮ as one flex item that takes the rest of the row and
            wraps as a block (min width) when the filters leave no room. */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ flex: '1 1 280px', minWidth: { xs: '100%', sm: 240 } }}
        >
          {search && (
            // `placeholder`, not `label`: a startAdornment forces MUI's floating
            // label into the permanently-shrunk position (so it never overlaps
            // the icon), which reads as "this field already has a value" even
            // when empty. A plain placeholder avoids that.
            <TextField
              placeholder={search.label}
              value={search.value}
              onChange={(event) => search.onChange(event.target.value)}
              sx={{ flex: 1 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: search.value && (
                    <InputAdornment position="end">
                      <IconButton size="small" edge="end" aria-label={clearLabel} onClick={() => search.onChange('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
          {endAction}
          {showGridControls && (
            <>
              <GridCompactDensitySync />
              <GridActionsMenu {...gridSettings} />
            </>
          )}
        </Stack>
      </Stack>
      <ActiveFiltersBar chips={chips} resultsLabel={resultsLabel} clearLabel={clearLabel} onClearAll={onClearAll} />
      {bulkAction && (
        <Stack direction="row" justifyContent="flex-end">
          {bulkAction}
        </Stack>
      )}
    </Box>
  );
}
